/**
 * MobileShell — phone chrome for the onsite portal (below the `md` breakpoint).
 *
 * Header: trip / property name, dark-mode toggle, sign-out. Bottom nav: four
 * primary destinations plus a "More" sheet holding the remaining routes, so
 * every page is reachable. The document scrolls; header and nav are fixed.
 *
 * The bottom nav height (plus the iOS home-indicator inset) is exposed as
 * --onsite-nav-height so main content, toasts and the sync indicator can sit
 * above it.
 */
import React, { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { navPathMatches } from '@pazportal/ui';
import {
  MOBILE_NAV_MORE,
  MOBILE_NAV_MORE_ICON,
  MOBILE_NAV_PRIMARY,
  PORTAL_COLOR,
  PORTAL_SUBTITLE,
  type MobileNavItem,
} from '../config/navigation';

export const MOBILE_NAV_HEIGHT = '3.75rem';
/** Full bottom offset for fixed elements: nav height + iOS safe area. */
export const MOBILE_BOTTOM_OFFSET = `calc(${MOBILE_NAV_HEIGHT} + env(safe-area-inset-bottom, 0px))`;

const NAV_FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80';

/** React 18 has no typed `inert` prop; an empty string sets the boolean attribute. */
function inertProps(inert: boolean): Record<string, string | undefined> {
  return { inert: inert ? '' : undefined };
}

function isModifiedClick(e: React.MouseEvent): boolean {
  return e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
}

export interface MobileShellProps {
  title: string;
  subtitle: string;
  userLabel: string;
  currentPath: string;
  /** Adds the ?trip= query so deep links keep the selected trip. */
  hrefFor: (href: string) => string;
  onNavigate: (href: string) => void;
  onSignOut: () => void;
  isDark: boolean;
  onToggleDark: () => void;
  children: ReactNode;
}

export function MobileShell({
  title,
  subtitle,
  userLabel,
  currentPath,
  hrefFor,
  onNavigate,
  onSignOut,
  isDark,
  onToggleDark,
  children,
}: MobileShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const sheetId = useId();
  const sheetTitleId = useId();

  const moreActive = MOBILE_NAV_MORE.some((item) => navPathMatches(currentPath, item.href));

  // Close the sheet whenever the route changes.
  useEffect(() => {
    setMoreOpen(false);
  }, [currentPath]);

  // Scroll lock + Escape + focus management while the sheet is open.
  useEffect(() => {
    if (!moreOpen) {
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
        moreButtonRef.current?.focus({ preventScroll: true });
      }
      return;
    }
    wasOpenRef.current = true;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMoreOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const frame = window.requestAnimationFrame(() => sheetRef.current?.focus({ preventScroll: true }));
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
      window.cancelAnimationFrame(frame);
    };
  }, [moreOpen]);

  const go = (href: string) => {
    setMoreOpen(false);
    onNavigate(href);
  };

  const renderLink = (item: MobileNavItem, variant: 'bar' | 'sheet') => {
    const active = navPathMatches(currentPath, item.href);
    const base =
      variant === 'bar'
        ? `flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-1.5 text-[11px] font-medium leading-tight transition-colors ${NAV_FOCUS_RING}`
        : `flex min-h-[48px] items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors ${NAV_FOCUS_RING}`;
    const tone = active ? 'text-white' : 'text-white/70 hover:text-white';
    return (
      <a
        key={item.href}
        href={hrefFor(item.href)}
        aria-current={active ? 'page' : undefined}
        onClick={(e) => {
          if (isModifiedClick(e)) return;
          e.preventDefault();
          go(item.href);
        }}
        className={`${base} ${tone} ${variant === 'sheet' && active ? 'bg-white/10' : ''}`}
      >
        <svg
          className={variant === 'bar' ? 'h-5 w-5' : 'h-5 w-5 shrink-0'}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
        </svg>
        <span className="truncate">{item.label}</span>
      </a>
    );
  };

  return (
    <div
      className="flex min-h-[var(--paz-vh,100vh)] flex-col bg-bg"
      style={{ '--onsite-nav-height': MOBILE_BOTTOM_OFFSET } as React.CSSProperties}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[70] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-text-inverse"
      >
        Skip to content
      </a>

      {/* ─── Header ─────────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-30 flex items-center gap-1 px-3 pt-[env(safe-area-inset-top,0px)] text-white shadow-md"
        style={{ backgroundColor: PORTAL_COLOR }}
        {...inertProps(moreOpen)}
      >
        <div className="flex min-h-[56px] min-w-0 flex-1 flex-col justify-center py-1">
          <p className="truncate text-[11px] font-semibold uppercase tracking-wider text-white/60">{PORTAL_SUBTITLE}</p>
          <h1 className="truncate text-base font-bold leading-tight">{title}</h1>
          <p className="truncate text-xs text-white/75">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onToggleDark}
          aria-label="Dark mode"
          aria-pressed={isDark}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-white/85 hover:bg-white/10 hover:text-white ${NAV_FOCUS_RING}`}
        >
          {isDark ? (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          )}
        </button>
        <button
          type="button"
          onClick={onSignOut}
          aria-label={`Sign out ${userLabel}`}
          title="Sign out"
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-white/85 hover:bg-white/10 hover:text-white ${NAV_FOCUS_RING}`}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          <span className="sr-only">Sign out</span>
        </button>
      </header>

      {/* ─── Content ────────────────────────────────────────────────── */}
      <main
        id="main"
        tabIndex={-1}
        className="min-w-0 flex-1 px-4 pt-4 focus:outline-none"
        style={{ paddingBottom: `calc(${MOBILE_BOTTOM_OFFSET} + 1.5rem)` }}
        {...inertProps(moreOpen)}
      >
        {children}
      </main>

      {/* ─── "More" sheet ───────────────────────────────────────────── */}
      {moreOpen && (
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMoreOpen(false)} aria-hidden="true" />
      )}
      <div
        ref={sheetRef}
        id={sheetId}
        role="dialog"
        aria-modal={moreOpen ? true : undefined}
        aria-labelledby={sheetTitleId}
        aria-hidden={!moreOpen || undefined}
        tabIndex={-1}
        {...inertProps(!moreOpen)}
        className={`fixed left-0 right-0 z-50 rounded-t-2xl px-3 pb-3 pt-2 text-white shadow-2xl transition-[transform,visibility] duration-200 ease-out focus:outline-none ${
          moreOpen ? 'visible translate-y-0' : 'invisible translate-y-full'
        }`}
        style={{ bottom: MOBILE_BOTTOM_OFFSET, backgroundColor: PORTAL_COLOR }}
      >
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-white/30" aria-hidden="true" />
        <div className="flex items-center justify-between px-1">
          <h2 id={sheetTitleId} className="text-sm font-semibold">
            More pages
          </h2>
          <button
            type="button"
            onClick={() => setMoreOpen(false)}
            aria-label="Close menu"
            className={`-mr-1 flex h-11 w-11 items-center justify-center rounded-md text-white/85 hover:bg-white/10 ${NAV_FOCUS_RING}`}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav aria-label="More pages" className="mt-1 flex flex-col gap-0.5">
          {MOBILE_NAV_MORE.map((item) => renderLink(item, 'sheet'))}
        </nav>
        <p className="mt-3 truncate px-3 text-xs text-white/60">Signed in as {userLabel}</p>
      </div>

      {/* ─── Bottom navigation ──────────────────────────────────────── */}
      <nav
        aria-label="Primary"
        className="fixed bottom-0 left-0 right-0 z-50 flex items-stretch border-t border-white/10 pb-[env(safe-area-inset-bottom,0px)]"
        style={{ backgroundColor: PORTAL_COLOR, minHeight: MOBILE_BOTTOM_OFFSET }}
        {...inertProps(moreOpen)}
      >
        {MOBILE_NAV_PRIMARY.map((item) => renderLink(item, 'bar'))}
        <button
          ref={moreButtonRef}
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-controls={sheetId}
          aria-current={moreActive && !moreOpen ? 'page' : undefined}
          className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-1.5 text-[11px] font-medium leading-tight transition-colors ${NAV_FOCUS_RING} ${
            moreActive || moreOpen ? 'text-white' : 'text-white/70 hover:text-white'
          }`}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d={MOBILE_NAV_MORE_ICON} />
          </svg>
          <span>More</span>
        </button>
      </nav>
    </div>
  );
}
